// =============================================================================
// ui/tabs/Analytics.tsx — the Overview sub-tab.
// -----------------------------------------------------------------------------
// ── 🚨 ANALYTICS IGNORES THE GLOBAL DATE FILTER ──
//
// It has its OWN period chips (1W / 1M / 3M / YTD / 1Y / All + custom), and the
// old client DISABLES the global date control while an analytics tab is open,
// with a tooltip saying why. This tab therefore does NOT call `useRange()` for
// its scope — `useRange` is in scope and using it would look entirely correct,
// which is exactly why the period lens is re-exported separately (see
// selectors/analytics.ts).
//
// It reads the ACCOUNT lens and nothing else. ⚠ It does NOT read the FACET lens,
// which an earlier version of this file did on the reasoning that "those are not
// date filters and the old client applies them everywhere via `tradesForActive`"
// — beta's analytics population does not go through `tradesForActive`. It is
// `tradesForActiveAccounts().filter(countsForMetrics)`, and beta's own comment on
// the facet layer says it is "applied ONLY through tradesForActive()".
//
// ── PARTIAL: OVERVIEW, DOWN AS FAR AS RISK-ADJUSTED RETURNS ──
//
// The old client has five analytics sub-tabs (Overview, Setup Quality,
// Excursion, Prop Report, Custom Analytics) and each is its own sidebar tab
// here. This is Overview, and within it, so far:
//
//   the period bar · the hero · the four KPI tiles · Performance (the equity and
//   drawdown curves side by side, and risk-adjusted returns)
//
// Still to come, and named on screen rather than left blank: the R-multiple
// distribution, the day-by-hour heatmap, and the whole Discipline section.
//
// 🔴 THERE WAS ALSO A "P&L BY WEEKDAY" CARD HERE AND THE OLD CLIENT HAS NO SUCH
// THING ON THIS TAB — it belongs to the Dashboard. It arrived by reading beta's
// source; Josh's screenshot is what caught it. **Presence in the old client's
// source is not evidence a control is on that screen.**
// =============================================================================

import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { Icon } from '../shell/Icon';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue } from '../../selectors/pnlUnit';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { useFilters } from '../state/filters';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import {
  ANALYTICS_PERIODS, DEFAULT_ANALYTICS_PERIOD, analyticsPeriodLabel,
  controllableLossTrades, dateInAnalyticsPeriod, equityCurve, periodCutoff,
  averageR, cumulativeWinRates, dailyBuckets, peakEquity, runningPeak,
  trendVsPrior, underwater,
  MIN_TRADING_DAYS, riskAdjusted,
  RDIST_MIN_SAMPLE, RDIST_POSITIVE_FROM, rMultipleDistribution,
  DOW_LABELS, buildHeatmap, cellKey, heatIntensity, heatMax, heatValue, hourLabel,
  ADHERENCE_SERIES, MISTAKE_SERIES, processMetrics, weekStartMonday, weekLabel,
  TILT_MIN_SAMPLE, mistakeClustering, tiltSignal,
  NTH_CAP, controllableLoss,
  type ControllableLoss, type ClPoint,
  type AnalyticsPeriod, type OverviewTrade,
  type EquityPoint, type RatioReading, type RiskAdjusted, type RiskVerdict,
  type DistShape, type RDistribution, type HeatGrid, type HeatMetric,
  type ProcessWeek, type MistakeRow, type TiltSignal,
} from '../../selectors/analytics';

import {
  dashAdjustmentLegs, dashAdjustmentTotal,
} from '../../selectors/dashAdjustments';
import { tradesCountingForMetrics, type Account } from '../../selectors/accounts';
import { effectiveTradeDate } from '../../selectors/population';
import { selectKpis, type Kpis } from '../../selectors/kpis';
import type { TradeTableRow } from '../../io/sync/trades';
import { money, moneyPlain, num, pct, unitValue } from '../format';
import { CoachDrill, type DrillTrade } from './CoachDrill';

/**
 * ⚠ ALL TIME, deliberately. Analytics' own period does the narrowing in memory,
 * so the READ has to be unbounded or `All` would silently mean "the active range"
 * — the precise confusion this tab exists apart from.
 */
const UNBOUNDED = { startDate: null, endDate: null };

export function AnalyticsTab() {
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  // The display unit. This tab converts its PERIOD numbers; the leak section
  // below stays in dollars by design — see the note there (#44).
  const { unit } = usePnlUnit();
  // ⚠ `facets` deliberately NOT read — see the population memo below.
  const { accounts: selection } = useFilters();
  // The global display unit — $ / points / ticks / R. See `UnitPicker`.
  const { prefs } = usePreferences();
  // ⚠ `today` ONLY — never `bounds`. The anchor is needed to resolve "1W"; the
  // global range is not applied here at all.
  const { today } = useRange();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [period, setPeriod] = useState<AnalyticsPeriod>(DEFAULT_ANALYTICS_PERIOD);
  const { phase, rows, error } = useTradeRows(UNBOUNDED, mode);

  // account + exclude-from-metrics → PERIOD. NOT the global date range, and NOT
  // the facets — beta's analytics population is
  // `tradesForActiveAccounts().filter(countsForMetrics)`, which bypasses both
  // lenses. See `tradesCountingForMetrics`.
  const inPeriod = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? []);
    return scoped.filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today));
  }, [rows, mode, today, selection, accounts, period]);

  const kpis = useMemo<Kpis<TradeTableRow> | null>(() => {
    if (!inPeriod || !accounts || !contracts) return null;
    return selectKpis({
      trades: inPeriod,
      accounts: accounts as readonly Account[],
      contracts,
      selection,
      unit,
    });
  }, [inPeriod, accounts, contracts, selection]);

  const tradeLegs = useMemo(() => {
    if (!inPeriod || !mode) return null;
    // ⚠ CHRONOLOGICAL, by the same `date + timeIn` key computeKpis walks — the
    // curve and the max-drawdown figure must be the same traversal or they
    // disagree about the same day.
    // 🚨 THE EFFECTIVE DATE, WHICH IS NOT THE CALENDAR ONE. The old client's
    // analytics curve orders by `t.core.date` while its own period scoping,
    // sparklines and heatmap all use the effective date — so on a book with
    // evening trades the curve takes a different path and the Peak Equity tile
    // lands on a different trade. $12 of difference on Josh's book, over the 101
    // of his 495 trades whose session date is not their calendar date. PD-17.
    const chron = [...inPeriod].sort((a, b) =>
      `${effectiveTradeDate(a, mode)} ${a.timeIn ?? ''}`
        .localeCompare(`${effectiveTradeDate(b, mode)} ${b.timeIn ?? ''}`));
    //  ── 🚨 IN THE DISPLAY UNIT, AND THE FIELD WAS ALWAYS CALLED THAT ──
    //
    // `unitValue` has been handed `t.netPnl` since the port — dollars, under a
    // name that promised otherwise. Peak Equity and Max Drawdown both come off
    // this curve, so they were dollars too however the toolbar was set.
    //
    // ⚠ TRADES WITH NO VALUE IN THE UNIT ARE DROPPED, not zeroed. In R a
    // stop-less trade has no realised R; a zero would be a flat step on the
    // curve, which reads as a scratch that did not happen. `computeKpis` skips
    // exactly these in its own drawdown walk, and this comment two lines up
    // requires the two traversals to agree — so dropping them is what KEEPS
    // the tile and the curve in step, not a divergence from it.
    // ⚠ THE LEGS ARE THEIR OWN VALUE so the adjusted curve can be built from the
    // SAME list rather than reconstructed from this one's output points. Two
    // curves derived from one leg list cannot disagree about a trade.
    return chron.flatMap((t) => {
      const v = tradeValue(t as never, unit, contracts ?? []);
      if (v == null) return [];
      return [{
        id: t.id, date: effectiveTradeDate(t, mode), timeIn: t.timeIn as string | null,
        contract: t.contract as string | null, unitValue: v,
      }];
    });
  }, [inPeriod, mode, unit, contracts]);

  const curve = useMemo(
    () => (tradeLegs ? equityCurve(tradeLegs) : null),
    [tradeLegs],
  );

  /**
   * ── 🔴 PAYOUTS AND DEPOSITS NEVER REACHED THIS TAB (#89) ──
   *
   * D210 folded account adjustments into the Dashboard and stopped there. This
   * screen went on computing everything from trades alone, so a funded trader's
   * payout — the event they most want to see on an equity curve — simply did not
   * appear, and the two tabs disagreed about the same account over the same
   * period. Nothing on screen said adjustments were excluded, which is why it
   * was invisible from the tab itself.
   *
   * ⚠ SAME TWO FUNCTIONS THE DASHBOARD USES, deliberately. `dashAdjustmentLegs`
   * already scopes to the selected accounts and a date range, and already drops
   * eval / reset / activation FEES via `isBalanceMoving` — those are money out of
   * the trader's bank, not trading performance. A second implementation here is
   * how the two tabs would drift apart again.
   *
   * ⚠ DOLLARS ONLY, and beta gates the same way for the same reason: cash does
   * not convert to points, ticks or R. In any other unit this is empty and the
   * screen is trade-only, exactly as before.
   *
   * ⚠ SCOPED BY THE PERIOD CHIP, not the global date filter — this tab's own
   * period is the sole date authority here, which is the rule the trade scoping
   * above already follows.
   */
  const adjLegs = useMemo(
    // ⚠ `today` is null until the range provider settles; with no anchor there
    // is no period to scope adjustments to, and an unscoped list would briefly
    // show every payout the account has ever taken.
    () => (unit === 'usd' && today
      ? dashAdjustmentLegs(accounts ?? [], selection, {
        startDate: periodCutoff(period, today) ?? null, endDate: null,
      })
      : []),
    [unit, accounts, selection, period, today],
  );

  /**
   * 🚨 A SECOND CURVE, AND THE SPLIT IS THE WHOLE POINT.
   *
   * Beta's comment on this exact block says it plainly: "Equity LINE + Period
   * P&L + Peak include account adjustments scoped to this period; the drawdown
   * chart + Max Drawdown stay trade-only — **a payout isn't a trading
   * drawdown**."
   *
   * That distinction is why this is not a one-line addition. Withdrawing money
   * drops the equity line, and a single combined curve would report that drop as
   * a drawdown — turning a successful payout into a risk event, inflating Max
   * Drawdown and deflating the Calmar ratio computed from it.
   *
   * So: `curve` stays trade-only and keeps feeding the drawdown chart, Max
   * Drawdown and Calmar. `equityAdjCurve` is the combined one, and only the
   * three figures beta names read from it.
   */
  const equityAdjCurve = useMemo(() => {
    if (!tradeLegs) return null;
    if (!adjLegs.length) return curve;
    const legs = [
      ...tradeLegs,
      // ⚠ AN EMPTY TIME, so an adjustment sorts BEFORE a trade on a shared date —
      // beta's cumulator holds the same rule, and it is what lets a carry-in seed
      // the curve before the day's trading rather than land on top of it.
      // ⚠ THE ID IS THE INDEX, NOT A CONTENT TEMPLATE, and that is deliberate.
      // `EquityPoint.id` is not read anywhere on this tab — it exists so a trade
      // point can be traced back to its trade — and a templated string here
      // trips `persistedIdShape`, the guard that stops an id derived from content
      // reaching a table with a GLOBAL primary key (D179). This id is never
      // persisted, but a shape that reads like one is worth not writing.
      ...adjLegs.map((a, i) => ({
        id: i, date: a.date, timeIn: '', contract: null, unitValue: a.amount,
      })),
    ].sort((x, y) => `${x.date} ${x.timeIn ?? ''}`.localeCompare(`${y.date} ${y.timeIn ?? ''}`));
    return equityCurve(legs);
  }, [tradeLegs, curve, adjLegs]);

  /** The adjustment money as PRINTED — see the unit note on `adjLegs`. */
  const adjTotal = useMemo(() => dashAdjustmentTotal(adjLegs), [adjLegs]);

  /**
   * ── 🚨 THE DRILL, AND WHY ITS TRADES COME FROM THE DOMAIN ──
   *
   * Beta's leak and worst-day rows open the trades behind them; these were inert
   * divs (#78). The obvious wiring is to re-filter `inPeriod` right here — and it
   * is the wrong one: that is a SECOND copy of `isControllableLoss`, and the
   * first time the two disagree the reader clicks a row saying "13 trades" and
   * gets a table of eleven, with nothing on screen to say which is right.
   *
   * ▶ Not hypothetical. That is what #88 found between the Day Report and
   * Excursion, and #89 between the Dashboard and Analytics — one number computed
   * in two places, twice in a week. So the row and its drill come out of ONE
   * function, and a test asserts the count on the row equals the length of the
   * table it opens.
   */
  const [drill, setDrill] = useState<
    { by: { reason: string } | { date: string }; label: string } | null
  >(null);

  /**
   * ⚠ THE TAG CONFIG COMES FROM THE TRADER'S SETTINGS. A tag nobody has
   * configured counts as controllable — the setting exists to exclude, not to
   * opt in — so passing `null` here would be right only by luck.
   */
  const clConfig = useMemo(() => (
    (prefs?.appFlags?.['controllableMistakeTags'] as Record<string, boolean> | undefined) ?? null
  ), [prefs]);

  const drillTrades = useMemo(() => {
    if (!drill || !inPeriod || !mode) return null;
    // ⚠ THE EFFECTIVE DATE, as everywhere else on this tab — a worst-day row is
    // keyed by the session day, so matching on the calendar one would open the
    // wrong evening's trades. PD-17.
    const withDate = inPeriod.map((t) => ({ ...t, date: effectiveTradeDate(t, mode) }));
    return controllableLossTrades(
      // 🚨 THE SAME CONFIG OBJECT THE CARD USES, and the first draft of this
      // line read `prefs.controllableMistakeTags` — a property that does not
      // exist, so it resolved to null. The rule treats an UNCONFIGURED tag as
      // controllable ("the setting exists to exclude, not to opt in"), so null
      // is wrong by luck rather than obviously wrong: the drill would have
      // quietly disagreed with the row it opened from, on a book with any
      // custom mistake tag. That is the exact defect this wiring exists to
      // avoid, reintroduced by a typo, and tsc could not see it.
      withDate as never, drill.by, clConfig,
    );
  }, [drill, inPeriod, mode, clConfig]);

  // ── The hero and its four tiles ────────────────────────────────────────────
  const overview = useMemo<OverviewTrade[] | null>(() => {
    if (!inPeriod || !mode) return null;
    return inPeriod.map((t) => ({
      date: effectiveTradeDate(t, mode),
      netPnl: t.netPnl,
      rrRealized: t.rrRealized,
    }));
  }, [inPeriod, mode]);

  const days = useMemo(() => (overview ? dailyBuckets(overview) : null), [overview]);
  const avgR = useMemo(() => (overview ? averageR(overview) : null), [overview]);
  const dist = useMemo<RDistribution | null>(
    () => (inPeriod ? rMultipleDistribution(inPeriod.map((t) => ({ rrRealized: t.rrRealized }))) : null),
    [inPeriod],
  );

  // ── Timing ─────────────────────────────────────────────────────────────────
  const [heatMetric, setHeatMetric] = useState<HeatMetric>('pnl');
  const grid = useMemo<HeatGrid | null>(() => {
    if (!inPeriod || !mode) return null;
    // ⚠ The ROW is the effective date's weekday; the COLUMN is the raw opening
    // hour. A 23:00 Monday trade is in Tuesday's row and the 11pm column.
    // 🚨 IN THE DISPLAY UNIT. It was raw `netPnl`, so with the toolbar on Points
    // the headline read "+12.5 pts" while a cell six inches below read "+$50" —
    // two units on one screen with nothing reconciling them. Beta converts the
    // cells and the hover text (app/index.html:43756).
    //
    // ⚠ A trade with no value in this unit — no stop for R, an uncatalogued
    // contract for points — contributes NOTHING rather than zero. A zero would
    // pull a cell's colour toward neutral and read as "flat here".
    return buildHeatmap(inPeriod.map((t) => ({
      date: effectiveTradeDate(t, mode),
      timeIn: t.timeIn as string | null,
      netPnl: tradeValue(t as never, unit, contracts ?? []),
    })));
  }, [inPeriod, mode, unit, contracts]);

  /**
   * 🚨 THE DRAWDOWN COMES OFF THE CURVE THIS SCREEN DRAWS, not from `selectKpis`.
   *
   * `selectKpis` walks its population sorted by `t.date` — the CALENDAR date —
   * while this tab's curve is walked by the EFFECTIVE date, because that is the
   * day model every other figure on the tab uses. Two traversals of one book
   * give two drawdowns: on Josh's own data the tile read −$6,862.53 and the
   * chart beneath it −$6,850.53, twelve dollars apart, both on screen at once.
   *
   * Deriving it from `curve` makes the tile, the chart and the Calmar ratio the
   * same array rather than three walks that agree by luck. D102.
   */
  const maxDD = useMemo(
    () => (curve ? Math.max(0, ...curve.map((p) => -p.drawdown)) : 0),
    [curve],
  );

  // ⚠ The EFFECTIVE date again, for the same reason the curve uses it — the old
  // client buckets these daily returns by `t.core.date`. PD-17.
  const risk = useMemo<RiskAdjusted | null>(() => {
    if (!overview || !kpis) return null;
    return riskAdjusted(overview, maxDD, kpis.totalPnl);
  }, [overview, kpis, maxDD]);

  /**
   * The same window, shifted back by its own length.
   *
   * ── 🚨 GATED ON THE PRESET HAVING A LENGTH, NOT ON A LIST OF IDS ──
   *
   * This read `period.id === 'all' || period.id === 'custom'`, and the comment
   * above it already said "only for the FIXED-LENGTH presets" — but **YTD is not
   * fixed-length and was not excluded.** `periodCutoff` returns Jan 1 for it, so
   * a prior window got computed and the pill rendered a comparison against a
   * window that does not mean anything: the same number of days before January,
   * which is a slice of last year corresponding to nothing a trader asked about.
   *
   * ▶ Beta's rule, read rather than grepped (`computePriorPnl`,
   * `app/index.html:44012`): `if (!periodObj.days) return null;`. Its `ytd`
   * preset carries no `days` and neither does `all`, so both are excluded by the
   * same test. Ported as the SEMANTIC — ask the definition whether the window has
   * a length — not as a longer blacklist.
   *
   * ⚠ AN ID LIST IS EXACTLY WHAT ROTTED HERE. It named the two presets somebody
   * thought of and silently admitted the third. Anything added to
   * `ANALYTICS_PERIODS` without a `days` is now excluded automatically, and
   * `custom` is not in that list at all so it still returns null.
   */
  const priorPnl = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const def = ANALYTICS_PERIODS.find((p) => p.id === period.id);
    if (!def?.days) return null;
    const cutoff = periodCutoff(period, today);
    if (!cutoff) return null;
    // ⚠ The definition's own length, so the guard and the span cannot drift.
    const priorStart = shiftDays(cutoff, -def.days);
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? []);
    let sum = 0; let n = 0;
    for (const t of scoped) {
      const d = effectiveTradeDate(t, mode);
      if (!d || d < priorStart || d >= cutoff) continue;
      // 🚨 IN THE DISPLAY UNIT, because this number is COMPARED against one.
      //
      // It was `t.netPnl` — raw dollars — while `kpis.totalPnl` on the other
      // side of the comparison is unit-converted. The pill then divided points
      // by dollars and printed the result as a percentage, so on a $5-a-point
      // contract a period that genuinely improved 25% could read "▼ −75%".
      //
      // ⚠ THE ONLY ONE OF #67's ELEVEN SITES THAT COULD INVERT A SIGN. The
      // others mislabel a number; this one changed which direction the arrow
      // pointed. Beta converts the prior period too (app/index.html:43991).
      const v = tradeValue(t as never, unit, contracts ?? []);
      if (v == null) continue;
      sum += v;
      n += 1;
    }
    // ⚠ NO TRADES IS NOT ZERO P&L. Returning 0 would make every first-ever
    // period read as an infinite improvement over a window nobody traded.
    return n ? sum : null;
  }, [rows, mode, today, period, selection, accounts, unit, contracts]);

  // ── Discipline ─────────────────────────────────────────────────────────────
  const weeks = useMemo<ProcessWeek[] | null>(() => {
    if (!inPeriod || !mode) return null;
    return processMetrics(inPeriod.map((t) => ({
      date: effectiveTradeDate(t, mode),
      annotated: t.annotated as boolean | null,
      booleans: t.booleans as Record<string, unknown> | null,
      mistakeTags: t.mistakeTags as readonly string[] | null,
    })));
  }, [inPeriod, mode]);

  const tilt = useMemo<TiltSignal | null>(() => {
    if (!inPeriod || !mode) return null;
    // ⚠ The EFFECTIVE date orders the sequence, as it orders the curve. The old
    // client sorts tilt by `t.core.date`, so an evening trade lands before the
    // morning session it actually followed.
    return tiltSignal(inPeriod.map((t) => ({
      date: effectiveTradeDate(t, mode),
      timeIn: t.timeIn as string | null,
      netPnl: t.netPnl,
    })));
  }, [inPeriod, mode]);

  const mistakes = useMemo<MistakeRow[] | null>(() => (
    inPeriod ? mistakeClustering(inPeriod.map((t) => ({
      booleans: t.booleans as Record<string, unknown> | null,
      mistakeTags: t.mistakeTags as readonly string[] | null,
    }))) : null
  ), [inPeriod]);

  const cl = useMemo<ControllableLoss | null>(() => {
    if (!inPeriod || !mode) return null;
    return controllableLoss(inPeriod.map((t) => ({
      date: effectiveTradeDate(t, mode),
      timeIn: t.timeIn as string | null,
      // ⚠ TOOLTIP ONLY. Nothing on this card is scoped or scaled by instrument —
      // see the dollars note inside it.
      contract: t.contract as string | null,
      netPnl: t.netPnl,
      booleans: t.booleans as Record<string, unknown> | null,
      mistakeTags: t.mistakeTags as readonly string[] | null,
      mfePoints: t.mfePoints,
      riskPoints: t.riskPoints,
      // 🚨 MONDAY, and NOT OPTIONAL to keep in step with Custom Analytics.
      // Overview and Custom render ONE `CostOfIndisciplineCard` precisely so the
      // two surfaces cannot disagree; fixing only one call site would put two
      // different weekly trends under one heading in one app.
    })), clConfig, weekStartMonday);
  }, [inPeriod, mode, clConfig]);

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your analytics</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {error}</p>
      </div>
    );
  }

  if (!kpis || !curve || !days || !avgR || !risk || !dist || !grid
    || !weeks || !tilt || !mistakes || !cl) {
    return <p className="muted">Loading your analytics…</p>;
  }

  // ⚠ THE COMBINED CURVE FEEDS THE EQUITY LINE AND PEAK EQUITY; `curve` still
  // feeds the drawdown chart and Max Drawdown. See the note on `equityAdjCurve`.
  const equityVals = (equityAdjCurve ?? curve).map((p) => p.equity);
  // 🚨 THE HEADLINE INCLUDES ADJUSTMENTS AND `kpis.totalPnl` DOES NOT, which is
  // the same split the Dashboard holds: folding a payout into the KPI would make
  // a losing period read as profitable in expectancy, profit factor and win
  // rate, all of which are trade-only measures of trading.
  const shownPnl = kpis.totalPnl + adjTotal;
  const trend = trendVsPrior(kpis.totalPnl, priorPnl);

  return (
    <div className="analytics-tab">
      <PeriodBar period={period} onPick={setPeriod} />

      {/* ⚠ Stated, because the global Filters button is DISABLED on this tab and a
          user who just set a date range needs to know why it did nothing. */}
      <p className="muted">
        Scoped to <b>{analyticsPeriodLabel(period)}</b> — Analytics uses its own
        period, so neither the global date filter nor the instrument, tag and
        pattern filters apply here. Your account selection does.
      </p>

      {kpis.count === 0 ? (
        <div className="card">
          <h3>No trades in {analyticsPeriodLabel(period)}</h3>
          <p className="muted">Pick a wider period above.</p>
        </div>
      ) : (
        <>
          <Hero
            unit={unit}
            mixed={kpis.mixed}
            valued={kpis.valued}
            label={analyticsPeriodLabel(period)}
            netPnl={shownPnl}
            trendPct={trend}
            equity={equityVals}
            count={kpis.count}
            winRate={kpis.winRate}
            avgR={avgR.avgR}
            avgRSample={avgR.sample}
            profitFactor={kpis.profitFactor}
            maxDrawdown={maxDD}
          />

          {/* 🚨 `periodPnl` IS TRADE-ONLY, NOT `shownPnl`, AND THAT DIFFERS FROM
              BETA'S LITERAL SOURCE. Beta passes its headline figure, which folds
              in payouts and deposits. Win rate is a trade-only measure of
              trading — the note on `shownPnl` above says exactly that about why
              the headline and the KPIs are split — so a deposit must not be able
              to turn "your losing period had a poor win rate" into silence.
              ⚠ A comment cannot sit between JSX attributes; it lives here for
              that reason, not by preference. */}
          <KpiTiles
            unit={unit}
            count={kpis.count}
            tradingDays={days.length}
            dailyCounts={days.map((d) => d.trades)}
            winRate={kpis.winRate}
            wins={kpis.wins}
            losses={kpis.losses}
            periodPnl={kpis.totalPnl}
            winRateSeries={cumulativeWinRates(days)}
            equity={equityVals}
            maxDrawdown={maxDD}
          />

          <div className="section-header exc-divider">
            <h3>Performance</h3>
            <span className="muted">what the period looked like, and what it cost</span>
          </div>

          {/* ⚠ SIDE BY SIDE, and that pairing is the point: the same period, once
              as what you made and once as how far under water you went to make
              it. Stacked, the second reads as an afterthought. */}
          <div className="an-perf-row">
            <CurveCard title="Equity curve" points={equityAdjCurve ?? curve}
              kind="equity" unit={unit} />
            <CurveCard title="Drawdown" points={curve} kind="drawdown" unit={unit} />
          </div>

          <RiskCard risk={risk} />
          <DistributionCard dist={dist} periodTrades={kpis.count} />

          <div className="section-header exc-divider">
            <h3>Timing</h3>
            <span className="muted">when in the week the money is made</span>
          </div>
          <HeatmapCard grid={grid} metric={heatMetric} onMetric={setHeatMetric} unit={unit} />

          <div className="section-header exc-divider">
            <h3 className="gold">Discipline</h3>
            <span className="muted">are you in control, or sabotaging the edge?</span>
          </div>
          {/* ⚠ TWO COLUMNS, and the pairing is the argument: the weekly process
              chart on the left is the slow view, the two signals on the right are
              the sharp ones. */}
          <div className="an-perf-row">
            <ProcessCard weeks={weeks} />
            <div className="an-stack">
              <CostOfIndisciplineCard cl={cl}
              onDrill={(by, label) => setDrill({ by, label })} />
              <TiltCard tilt={tilt} />
              <MistakeCard rows={mistakes} />
              {/* ⚠ OVERVIEW ONLY, which is where beta puts it. Custom Analytics'
                  Controllable Loss dashboard renders the insights card and not
                  this chart, and matching that is a port rather than a choice. */}
              <ControllableLossChartCard cl={cl} />
            </div>
          </div>
        </>
      )}

      {/* ⚠ THIS CARD WENT STALE AND SAID SO ON SCREEN. It claimed Setup Quality,
          Excursion and Prop Report "are not ported" for as long as all three
          have had their own sidebar tab and their own partial screens. A
          "what's missing" note is a CLAIM like any other, and it rots faster
          than the code it describes — it is the one piece of copy that has to
          be re-read every time something ships. */}
      <div className="card">
        <h3>Not on this screen yet</h3>
        {/* 🔴 BOTH SENTENCES THAT USED TO BE HERE WERE FALSE, and the second one
            carried a comment reading "⚠ VERIFIED, not assumed … checked before
            writing this sentence, because 'is not built at all' is exactly the
            kind of claim that turns out to be a month out of date."

            It was a month out of date. Custom Analytics shipped four days after
            that sentence was written and has been routed ever since. And the
            first sentence called the whole Discipline section "still to come"
            while its four cards rendered TWENTY LINES ABOVE IT — a reader
            scrolled past them and was then told they did not exist.

            ▶ So the re-read-it-yourself instruction is gone, replaced by
            `notYetCards.test.ts`, which fails the build when a card names a
            section this file renders or a tab `App.tsx` routes. A paragraph
            about what is missing cannot be maintained by good intentions: it is
            the one piece of copy whose accuracy decreases as the work goes
            well. */}
        {/* 🔴 AND IT WENT STALE AGAIN THE VERY NEXT DAY, in the one direction
            `notYetCards.test.ts` cannot see.

            The paragraph that was here named two things: the running-cost chart,
            and clicking a leak or a worst day to open the trades behind it. The
            drill shipped the day #77 closed — so this card was already claiming
            a working feature was absent, ONE COMMIT after being rewritten for
            exactly that mistake. The guard passed throughout: a claim about
            BEHAVIOUR has no heading and no route to check it against, which the
            guard states about itself in as many words.

            ⚠ AND A TEST WAS HOLDING IT IN PLACE — `honesty about scope`
            required this card to say "clicking a leak", so correcting the screen
            would have turned the suite red. That is the third time a stale
            sentence on this one card has been pinned by an assertion.

            ▶ Both are built now, so the card names no feature at all. Nothing
            here can rot while there is nothing here to be wrong about. */}
        <p className="muted">
          Nothing, as of this build. The running cost of your own mistakes draws
          below, and a leak or a worst day opens the trades behind it — those
          were the last two things beta had on this screen that this one did not.
        </p>
        <p className="muted">
          Setup Quality, Excursion, Prop Report and Custom Analytics are their
          own tabs in the sidebar, and each says on its own screen what it is
          still missing.
        </p>
      </div>

      {/* ⚠ `onChanged` IS A NO-OP HERE, DELIBERATELY. The Coach's Report
          reloads after a save in this drill because the save changes a leak it
          computed; this tab's data comes from the range read that the shell
          owns, so there is nothing for it to re-read. */}
      {drill && drillTrades ? (
        <CoachDrill
          label={drill.label}
          trades={drillTrades as unknown as DrillTrade[]}
          column={null}
          onClose={() => setDrill(null)}
          onChanged={() => {}}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The R-multiple distribution
// ---------------------------------------------------------------------------

const SHAPE_HEADLINE: Record<DistShape, string> = {
  'fat-right': 'Fat right tail',
  'fat-left': 'Fat left tail',
  scratchy: 'Scratchy',
  balanced: 'Balanced',
};

const SHAPE_SUB: Record<DistShape, string> = {
  'fat-right': 'your winners run',
  'fat-left': 'losses running past your risk',
  scratchy: 'most trades land near nothing',
  balanced: 'no tail is doing the work',
};

/**
 * ⚠ THIS IS THE ONE CARD ON THE TAB THAT IS NOT AN AVERAGE, and that is why it
 * exists. A +0.19R mean is the same figure whether it came from thirty 3R
 * winners or two hundred trades that all landed near zero, and those are
 * opposite problems with opposite fixes.
 */
function DistributionCard(
  { dist, periodTrades }: { dist: RDistribution; periodTrades: number },
) {
  if (dist.sample < RDIST_MIN_SAMPLE) {
    return (
      <div className="card" data-testid="an-rdist">
        <h3>Where your outcomes land</h3>
        <p className="muted">
          {/* ⚠ The reason, not just the refusal — a trader whose trades mostly
              carry no planned risk needs to know that is what is missing. */}
          Needs at least {RDIST_MIN_SAMPLE} trades with a recorded R, and{' '}
          {dist.sample === 0 ? 'none of' : `only ${dist.sample} of`} the{' '}
          {periodTrades} in this period {dist.sample === 1 ? 'has' : 'have'} one. R
          is your result measured against the risk you planned, so a trade with no
          planned stop cannot be placed on this scale at all.
        </p>
      </div>
    );
  }

  const max = Math.max(1, ...dist.bins.map((b) => b.count));

  return (
    <div className="card" data-testid="an-rdist">
      <h3>Where your outcomes land</h3>

      <div className="an-tile-row">
        <Tile label="Sample" value={String(dist.sample)}
          sub={`${dist.winners} winners · of ${periodTrades} trades`} />
        <Tile label="Typical trade"
          value={dist.median == null ? '—' : signedR(dist.median)}
          sub="the middle one — half above, half below" />
        <Tile label="Average trade"
          value={dist.mean == null ? '—' : signedR(dist.mean)}
          sub="what each trade is worth on average" />
        <Tile label="Shape" value={SHAPE_HEADLINE[dist.shape]} sub={SHAPE_SUB[dist.shape]} />
      </div>

      <div className="an-dist">
        {dist.bins.map((b, i) => (
          <div className="an-dist-col" key={b.label}>
            <div className="an-dist-n">{b.count}</div>
            <div className="an-dist-bar" data-side={i >= RDIST_POSITIVE_FROM ? 'up' : 'down'}
              data-rank={i >= RDIST_POSITIVE_FROM ? i - RDIST_POSITIVE_FROM : RDIST_POSITIVE_FROM - 1 - i}
              style={{ height: `${Math.max(2, (b.count / max) * 140)}px` }}
              title={`${b.count} trade${b.count === 1 ? '' : 's'} · ${pct(b.share, 0)}`} />
            <div className="an-dist-x">{b.label}</div>
          </div>
        ))}
      </div>

      {/* 🚨 THE SHAPES ARE NOT EXCLUSIVE. The old client reports only the first
          that matches, so a book of big winners paid for by big losers reads
          exactly like one with only the winners. */}
      {dist.alsoQualifies.length ? (
        <p className="an-verdict warn">
          <b>Two things are true of this distribution at once.</b>{' '}
          {pct(dist.fatRightShare, 0)} of these trades made a full unit of risk or
          more, and {pct(dist.fatLeftShare, 0)} lost more than one — big winners
          paid for by big losers. Read it as both, not as either.
        </p>
      ) : null}

      <p className="muted exc-note">
        Each bar is a count of trades, grouped by what they made measured against
        the risk you planned. <b>Weight on the right</b> means your winners are
        being allowed to run. <b>Weight on the far left</b> means losses are
        getting past the stop you set — the expensive shape. <b>Everything
        bunched in the middle two bars</b> means a lot of trading for very little
        outcome. Read this beside the risk ratios above: those say whether the
        overall picture is favourable, this says where that is coming from.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discipline
// ---------------------------------------------------------------------------

/** ⚠ Literal colours per series, so the four lines are told apart by more than
 *  their order in a legend. */
const SERIES_COLOUR: Record<string, string> = {
  plan: 'var(--blue)', aPlus: '#7eb8ff', stop: 'var(--purple)', target: 'var(--gold)',
  revenge: 'var(--gold)', fomo: 'var(--orange)', overtraded: 'var(--purple)', tags: 'var(--text-3)',
};

/**
 * Adherence above, mistakes below, over the same weeks.
 *
 * 🚨 P&L CAN RISE WHILE ADHERENCE FALLS, and that is the whole reason this card
 * exists. A good week traded badly is the most dangerous week a trader has,
 * because the result rewards the process that produced it.
 */
function ProcessCard({ weeks }: { weeks: readonly ProcessWeek[] }) {
  if (!weeks.length) {
    return (
      <div className="card" data-testid="an-process">
        <h3>Process, week by week</h3>
        <p className="muted">No dated trades in this period to measure a process against.</p>
      </div>
    );
  }
  const anyReviewed = weeks.some((w) => w.reviewed > 0);
  return (
    <div className="card" data-testid="an-process">
      <h3>Process, week by week</h3>

      <div className="an-sub-h">Adherence — share of each week’s reviewed trades</div>
      {anyReviewed ? (
        <MultiLine weeks={weeks}
          series={ADHERENCE_SERIES.map((s) => ({ key: s.key, label: s.label }))}
          valueOf={(w, k) => w.adherence[k] ?? null}
          max={100} suffix="%" />
      ) : (
        // ⚠ NOT a flat line along zero. The old client plots `?? 0` here, which
        // draws total indiscipline on a period nobody has annotated yet. PD-21.
        <p className="muted">
          None of these trades has been reviewed yet, so there is nothing to say
          about whether the rules held. Open a trade and tick what happened.
        </p>
      )}

      <div className="an-sub-h">Mistakes — count per week</div>
      <MultiLine weeks={weeks}
        series={MISTAKE_SERIES.map((s) => ({ key: s.key, label: s.label }))}
        valueOf={(w, k) => w.mistakes[k] ?? 0}
        max={null} suffix="" />

      <p className="muted exc-note">
        The top chart is the share of each week’s <b>reviewed</b> trades where the
        rule held — a week you have not annotated has no share and shows a gap
        rather than a zero. The bottom is a raw count of what went wrong.{' '}
        <b>Watch for the two moving apart from your P&amp;L.</b> A green week with
        falling adherence is the most dangerous week you have, because the result
        rewards the process that produced it.
      </p>
    </div>
  );
}

/**
 * ⚠ A GAP IS A GAP. A null week breaks the line rather than being interpolated
 * across — joining two measured weeks through an unmeasured one draws a
 * continuity that was never observed.
 */
function MultiLine(
  { weeks, series, valueOf, max, suffix }: {
    weeks: readonly ProcessWeek[];
    series: readonly { key: string; label: string }[];
    valueOf: (w: ProcessWeek, key: string) => number | null;
    max: number | null;
    suffix: string;
  },
) {
  const W = 560; const H = 150; const PAD = 6;
  const hi = max ?? Math.max(1, ...weeks.flatMap((w) => series.map((s) => valueOf(w, s.key) ?? 0)));
  const x = (i: number): number => (weeks.length < 2 ? W / 2 : PAD + (i / (weeks.length - 1)) * (W - PAD * 2));
  const y = (v: number): number => H - PAD - (v / (hi || 1)) * (H - PAD * 2);

  return (
    <>
      <div className="an-line-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
          aria-label={`${series.map((s) => s.label).join(', ')} by week`}>
          <line x1="0" y1={y(0)} x2={W} y2={y(0)} stroke="var(--border-1)" strokeWidth="1" />
          {series.map((s) => {
            // ⚠ SEGMENTS, not one path. A null week ends the current run.
            const runs: string[] = [];
            let cur: string[] = [];
            weeks.forEach((w, i) => {
              const v = valueOf(w, s.key);
              if (v == null) { if (cur.length > 1) runs.push(cur.join(' ')); cur = []; return; }
              cur.push(`${cur.length === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`);
            });
            if (cur.length > 1) runs.push(cur.join(' '));
            return runs.map((d, n) => (
              <path key={`${s.key}-${n}`} d={d} fill="none" strokeWidth="1.8"
                stroke={SERIES_COLOUR[s.key] ?? 'var(--text-2)'} strokeLinejoin="round"
                vectorEffect="non-scaling-stroke" />
            ));
          })}
        </svg>
      </div>
      <div className="an-legend">
        {series.map((s) => (
          <span className="an-legend-i" key={s.key}>
            <i style={{ background: SERIES_COLOUR[s.key] ?? 'var(--text-2)' }} />
            {s.label}
          </span>
        ))}
        <span className="an-legend-i muted">
          {weeks.length} week{weeks.length === 1 ? '' : 's'}
          {suffix === '%' ? ' · 0–100%' : ''}
        </span>
      </div>
    </>
  );
}

/**
 * What the preventable losses cost, and what they have in common.
 *
 * ── ⚠ ONE CARD, TWO SURFACES, AND THAT IS BETA'S OWN DESIGN ──
 *
 * This renders here on Overview AND on Custom Analytics' `Controllable Loss`
 * dashboard. The old client does the same thing with one function, and its own
 * comment gives the reason: the two "stay in sync". Two components measuring one
 * fact is how a screen ends up disagreeing with the screen next to it, which is
 * the class D102 and PD-13 are about.
 *
 * 🚨 THE CLEAN-VS-DIRTY SENTENCE FOLLOWS THE SIGN. The old client's reads
 * `Math.abs(cleanAvg - dirtyAvg)` beside the word "more", so a trader whose
 * clean days are WORSE is handed the size of that gap as evidence for the rule
 * it argues against. PD-22 — and confirmed live on Josh's 2026-08-11 shot of the
 * Custom Analytics panel: clean days −$105, days with a loss +$156, and beta
 * offers him "$261 more" as the case for stopping after one mistake.
 */
/**
 * ── 🔴 BETA'S FOUR SECTION GATES, NAMED SO THEY CANNOT DRIFT QUIETLY AGAIN ──
 *
 * All four had moved. Three were LOOSENED — so a book with one tagged loss drew
 * a distribution of it, one afternoon became "does one mistake snowball?", and
 * two weeks became a trend. The fourth moved the other way: the overtrading
 * warning fired at 50% where beta fires at 30%, which hid it from exactly the
 * trader it is written for.
 *
 * ⚠ READ OUT OF BETA, not out of the audit that reported them:
 *   :36076 `cl.length >= 5` · :36083 `latePct >= 30`
 *   :36101 `casDays >= 3`   · :36124 `clWeeks.length >= 3`
 *
 * ⚠ A THRESHOLD IS A JUDGEMENT ABOUT SAMPLE SIZE, not a tidiness setting. Each
 * one is the point below which the section would state something about the
 * trader that the data cannot support.
 */
const WHEN_MIN = 5;
const LATE_PCT_MIN = 30;
const CASCADE_MIN = 3;
const TREND_MIN = 3;
/** Beta draws the last twelve weeks and says so when it truncated (:36123). */
const TREND_MAX = 12;

export function CostOfIndisciplineCard(
  { cl, onDrill }: {
    cl: ControllableLoss;
    /**
     * Open the trades behind a row.
     *
     * ⚠ OPTIONAL, and its absence renders plain text rather than a dead control.
     * This card is exported and the Custom Analytics tab renders it too; a button
     * that does nothing there would be worse than no button. The row is only
     * clickable where something can actually open.
     */
    onDrill?: ((by: { reason: string } | { date: string }, label: string) => void) | undefined;
  },
) {
  if (cl.count === 0) {
    return (
      <div className="card" data-testid="an-cl">
        <h3>The cost of indiscipline</h3>
        <p className="muted">
          No losing trade in this period carries a revenge, FOMO or overtraded
          flag, an unhonoured stop, or a mistake tag. Either it was a clean
          period, or nothing has been annotated yet.
        </p>
      </div>
    );
  }

  const maxLeak = Math.max(1, ...cl.leaks.map((l) => l.total));
  const nthTotal = cl.byTradeNumber.reduce((s, r) => s + r.count, 0);
  const late = cl.byTradeNumber.find((r) => r.nth === NTH_CAP);
  const latePct = nthTotal ? Math.round((100 * (late?.count ?? 0)) / nthTotal) : 0;
  const cvd = cl.cleanVsDirty;

  return (
    <div className="card" data-testid="an-cl">
      <h3>The cost of indiscipline</h3>

      <div className="an-tile-row an-tile-row2">
        {/* ── 🚨 THIS SECTION IS IN DOLLARS ON PURPOSE, NOT PENDING ──

            The methodology this product is built on ranks leaks BY DOLLARS —
            "your leaks, ranked by dollars, biggest first" is the first line of
            the Spot-it/Fix-it/Prove-it copy in `domain/education`. Ranking them
            in points would put a 10-point MNQ leak ($20) above a 5-point ES one
            ($250) and invert the whole diagnosis.

            ⚠ So `controllableLoss` takes no unit, deliberately, and adding one
            later would be a product decision rather than a port. */}
        <Tile label="Without these trades" value={money(cl.aGame, 0)}
          sub={`you booked ${money(cl.net, 0)} · ${cl.count} preventable ${cl.count === 1 ? 'loss' : 'losses'} cost ${moneyPlain(cl.total, 0)}`} />
        {/* ⚠ NULL below three measurable trades, and the sample is stated —
            a share over two is not a share. */}
        <Tile label="Was already a winner"
          value={cl.gaveBackWinnerPct == null ? '—' : pct(cl.gaveBackWinnerPct, 0)}
          sub={cl.gaveBackWinnerPct == null
            ? `needs 3 with a stop and excursion data · ${cl.gaveBackSample} so far`
            : `of ${cl.gaveBackSample} measurable — had gone a full unit of risk your way first`} />
      </div>

      <div className="an-sub-h">Where the money goes — by dollars, not by count</div>
      {/* ⚠ A BUTTON ONLY WHERE IT OPENS SOMETHING — see `onDrill`. Beta's rows
          carry `data-cl-leak` and open the trades behind them; these were inert
          divs (#78). */}
      {cl.leaks.map((l) => (
        <div className={`an-bar2${onDrill ? ' an-bar2-click' : ''}`} key={l.reason}
          {...(onDrill ? {
            role: 'button', tabIndex: 0,
            title: `See the ${l.trades} trade${l.trades === 1 ? '' : 's'} behind this`,
            onClick: () => onDrill({ reason: l.reason }, l.reason),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill({ reason: l.reason }, l.reason); }
            },
          } : {})}>
          <div className="an-bar2-l">{l.reason}</div>
          <div className="an-bar2-t">
            <span className="an-bar2-f" data-tone="down"
              style={{ width: `${((l.total / maxLeak) * 100).toFixed(0)}%` }} />
          </div>
          <div className="an-bar2-v">
            {moneyPlain(l.total, 0)}
            <span className="muted"> · {l.trades}× {moneyPlain(l.per, 0)} ea</span>
          </div>
        </div>
      ))}
      <p className="muted exc-note">
        A trade with two reasons is counted under both, so these can add to more
        than {moneyPlain(cl.total, 0)}. The question each row answers is what that
        habit costs, not how to divide one loss.
      </p>

      {cl.worstDays.length ? (
        <>
          <div className="an-sub-h">Your worst days</div>
          {cl.worstDays.map((d) => (
            <div className={`an-bar2${onDrill ? ' an-bar2-click' : ''}`} key={d.date}
              {...(onDrill ? {
                role: 'button', tabIndex: 0,
                title: 'See the trades behind this day',
                onClick: () => onDrill({ date: d.date }, d.date),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill({ date: d.date }, d.date); }
                },
              } : {})}>
              {/* ⚠ THE REASONS, which beta names here and this card did not (#78).
                  "2026-07-24 · $1,240" tells the trader a day was bad; "revenge ·
                  overtraded" tells them what to do about it, which is the only
                  reason the row is on a discipline card. */}
              <div className="an-bar2-l">
                {d.date}
                {d.reasons.length
                  ? <span className="muted"> · {d.reasons.join(' · ')}</span>
                  : null}
              </div>
              <div className="an-bar2-t">
                <span className="an-bar2-f" data-tone="down"
                  style={{ width: `${((d.total / (cl.worstDays[0]?.total || 1)) * 100).toFixed(0)}%` }} />
              </div>
              <div className="an-bar2-v">{moneyPlain(d.total, 0)}</div>
            </div>
          ))}
        </>
      ) : null}

      {/* ── 🔴 FIVE PREVENTABLE LOSSES, WHICH IS BETA'S GATE ──
          `whenSection = cl.length >= 5` (index.html:36076). This read
          `nthTotal > 0`, so a book with ONE tagged loss got a bar chart of its
          distribution across the trading day — four bars, one of them full
          width, describing a single trade as a habit. */}
      {cl.count >= WHEN_MIN ? (
        <>
          <div className="an-sub-h">Where in the day it happens</div>
          {cl.byTradeNumber.map((r) => (
            <div className="an-bar2" key={r.nth}>
              <div className="an-bar2-l">
                {r.nth >= NTH_CAP ? `${NTH_CAP}th or later` : `${ordinal(r.nth)} trade`}
              </div>
              <div className="an-bar2-t">
                <span className="an-bar2-f" data-tone="warn"
                  style={{ width: `${((r.count / nthTotal) * 100).toFixed(0)}%` }} />
              </div>
              <div className="an-bar2-v">{r.count}</div>
            </div>
          ))}
          {latePct >= LATE_PCT_MIN ? (
            <p className="an-verdict warn">
              <b>{pct(latePct, 0)} of these are your {NTH_CAP}th trade of the day or
              later.</b> That is an overtrading tell — a hard daily trade cap would
              remove most of them without you having to judge anything in the moment.
            </p>
          ) : null}
        </>
      ) : null}

      {/* 🚨 THIS SECTION WAS COMPUTED AND RENDERED NOWHERE. `byHour` has been on
          the engine since it was ported and no surface drew it, while the old
          client shows it beside the trade-number bars on exactly this panel.
          Same shape as the four lessons already recorded: correct code that
          never ran, and nothing on earth fails when a value is simply unused. */}
      {cl.count >= WHEN_MIN && cl.byHour.length ? (
        <>
          <div className="an-sub-h">And at what time of day</div>
          {cl.byHour.map((h) => (
            <div className="an-bar2" key={h.hour}>
              <div className="an-bar2-l mono">{String(h.hour).padStart(2, '0')}:00</div>
              <div className="an-bar2-t">
                <span className="an-bar2-f" data-tone="warn"
                  style={{ width: `${((h.count / Math.max(1, ...cl.byHour.map((x) => x.count))) * 100).toFixed(0)}%` }} />
              </div>
              <div className="an-bar2-v">{h.count}</div>
            </div>
          ))}
          {/* ⚠ ONLY HOURS THAT HAVE ONE APPEAR, as the old client does. A run of
              zero-height rows for 12:00 and 13:00 would read as "measured, none"
              rather than "he does not trade then". */}
          <p className="muted exc-note">
            Only hours with a preventable loss are listed — a missing hour is an
            hour you did not trade, not a clean one.
          </p>
        </>
      ) : null}

      {cl.cascade.days >= CASCADE_MIN ? (
        <>
          <div className="an-sub-h">Does one mistake snowball?</div>
          <p className="muted exc-note">
            On <b>{cl.cascade.days}</b> day{cl.cascade.days === 1 ? '' : 's'} you kept
            trading after your first preventable loss.{' '}
            <b>{pct(cl.cascade.spiralledPct ?? 0, 0)}</b> of those finished net red
            afterwards, and what you traded after the mistake averaged{' '}
            <b className={signClass(cl.cascade.avgAfter ?? 0)}>{money(cl.cascade.avgAfter ?? 0, 0)}</b> a day.
          </p>
        </>
      ) : null}

      {cvd.comparable ? (
        <>
          <div className="an-sub-h">Clean days versus days with a preventable loss</div>
          <div className="an-tile-row an-tile-row2">
            <Tile label="Clean days" value={money(cvd.cleanAvg, 0)}
              sub={`${cvd.cleanDays} days · ${pct(cvd.cleanGreenPct, 0)} green`} />
            <Tile label="Days with one" value={money(cvd.dirtyAvg, 0)}
              sub={`${cvd.dirtyDays} days · ${pct(cvd.dirtyGreenPct, 0)} green`} />
          </div>
          {/* 🚨 THE SENTENCE FOLLOWS THE SIGN. See the card's header — the old
              client prints "worth X more" either way, on the strength of an
              absolute value. PD-22. */}
          <p className={`an-verdict${cvd.difference < 0 ? ' warn' : ''}`}>
            {cvd.difference > 0 ? (
              <><b>A clean day is worth {moneyPlain(cvd.difference, 0)} more</b> on
                average than a day you book a preventable loss. That is the case for
                “one mistake and I’m done”.</>
            ) : cvd.difference < 0 ? (
              <><b>Your clean days are actually {moneyPlain(-cvd.difference, 0)} worse</b> on
                average than the days you book a preventable loss. That is not an
                argument for trading sloppily — it usually means the days you make
                mistakes are your busy, high-opportunity days. But it is not the
                case for “one mistake and I’m done” either, and this card will not
                pretend otherwise.</>
            ) : (
              <>Your clean days and your days with a preventable loss average the
                same. No case either way from this period.</>
            )}
          </p>
        </>
      ) : null}

      {/* ── 🔴 THREE WEEKS TO GATE IT, TWELVE BARS TO DRAW ──
          Beta gates on the FULL week count and then slices:
          `trendSection = clWeeks.length >= 3`, `clWkShown = clWeeks.slice(-12)`
          (index.html:36123-36124). Two weeks is not a trend, and a year of them
          is fifty bars a few pixels wide each — a picture of nothing. */}
      {cl.weekly.length >= TREND_MIN ? (() => {
        const shown = cl.weekly.slice(-TREND_MAX);
        const tallest = Math.max(...shown.map((x) => x.total));
        return (
          <>
            <div className="an-sub-h">Are you cleaning it up?</div>
            <div className="an-spark-row">
              {shown.map((w) => (
                <div className="an-spark-col" key={w.weekStart}
                  title={`Week of ${weekLabel(w.weekStart)} — ${moneyPlain(w.total, 0)}`}>
                  {/* ⚠ SCALED AGAINST THE BARS ON SCREEN, not against every week
                      on record. Keeping the old denominator would leave a
                      truncated chart whose tallest visible bar is a stub because
                      the real peak was sliced off eighteen months ago. */}
                  <span className="an-spark-b" style={{
                    height: `${Math.max(2, (w.total / tallest) * 44)}px`,
                  }} />
                </div>
              ))}
            </div>
            <p className="muted exc-note">
              Preventable dollars per week
              {/* ⚠ ONLY WHEN IT ACTUALLY TRUNCATED — beta's own condition,
                  `clWeeks.length > clWkShown.length`. A permanent "(last 12)"
                  over four bars is a false statement about its own chart. */}
              {cl.weekly.length > shown.length ? ` (last ${shown.length})` : ''}, oldest on
              the left. Shorter bars — or none — are cleaner weeks.
            </p>
          </>
        );
      })() : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The running cost of your own mistakes
// ---------------------------------------------------------------------------

type ClView = 'cumulative' | 'per-trade';

const CL_VIEWS: readonly { id: ClView; label: string }[] = [
  { id: 'cumulative', label: 'Cumulative' },
  { id: 'per-trade', label: 'Per-trade' },
];

/** Chart box, in viewBox units. The CSS gives every `.an-chart svg` 180px. */
const CL_W = 560;
const CL_H = 180;

/**
 * The widest a single bar may be drawn.
 *
 * ⚠ THIS IS THE CONSTRAINT BETA ACTUALLY CARES ABOUT. Its comment gives the
 * reason for its own bar layout as "three losses must not become three planks" —
 * a cap on the bar does that, and does it whatever the box is.
 */
const CL_BAR_MAX = 20;

const clTitle = (p: ClPoint): string => [
  p.date ?? 'no date',
  p.contract,
  `${moneyPlain(p.amount, 0)} preventable`,
  `running ${moneyPlain(p.cumulative, 0)}`,
  p.reasons.length ? p.reasons.join(', ') : 'flagged by hand',
].filter(Boolean).join(' · ');

/**
 * What indiscipline has cost so far — as a running total, or one bar per loss.
 *
 * ── 🚨 THE LINE ENDS WHERE THE CARD ABOVE SAYS, BY CONSTRUCTION ──
 *
 * The last point's `cumulative` IS `cl.total`: the engine adds these losses once
 * and hands back both. It could just as easily have summed them a second time
 * here, and that is precisely the shape of the last three defects on this
 * screen — give-back computed on the Day Report and again on Excursion (#88),
 * the account balance on the Dashboard and again on Analytics (#89), and the
 * leak rule in the drill and again in the card (#78). Each was one number
 * computed in two places, and each drifted.
 *
 * ⚠ SO THIS COMPONENT DOES NO ARITHMETIC ON THE MONEY. It scales what it is
 * given to pixels and draws it. If the chart ever disagrees with the headline
 * the bug is in the engine, not in a second opinion held here.
 *
 * ── ⚠ DOLLARS, NOT THE DISPLAY UNIT ──
 *
 * The same deliberate choice the insights card makes, for the same reason: the
 * methodology ranks leaks by dollars, and the identical losses drawn in points
 * would stand a 10-point MNQ loss ($20) above a 5-point ES one ($250).
 */
function ControllableLossChartCard({ cl }: { cl: ControllableLoss }) {
  const [view, setView] = useState<ClView>('cumulative');

  // ⚠ NOTHING AT ALL rather than an empty frame. The insights card directly
  // above already says this period carries no preventable loss; a second card
  // repeating it reads as a chart that failed to draw.
  if (!cl.series.length) return null;

  const series = cl.series;
  const n = series.length;
  // ⚠ ONE LOSS IS NOT A RUNNING TOTAL — the rule the curves on this tab follow.
  // The toggle is withheld rather than offering a view that draws a single dot.
  const single = n < 2;
  const shown: ClView = single ? 'per-trade' : view;

  const peak = (series[n - 1] as ClPoint).cumulative;
  // ⚠ HEADROOM, so the last point is not welded to the top edge. It scales the
  // AXIS and never the value — `peak` is still drawn at `peak`.
  const yMax = peak * 1.06 || 1;
  const y = (v: number): number => CL_H - (v / yMax) * CL_H;
  const byHand = series.filter((pt) => pt.source === 'manual').length;

  const chart = shown === 'per-trade' ? (() => {
    // ── ⚠ ONE DEPARTURE FROM BETA, AND IT IS ABOUT BOX WIDTH ──
    //
    // Beta pins its bars to the LEFT at a fixed 28-unit slot inside a 1,200-unit
    // box, so a handful of losses occupy a fifth of the card and the rest is
    // blank. This box is 560 wide and sits in a column: measured on nine losses,
    // the bars stopped at x=248 and 57% of the chart was empty, which reads as a
    // chart that failed to draw rather than as a quiet period.
    //
    // ▶ So the slot spreads across the box and `CL_BAR_MAX` keeps the bars from
    // becoming planks — the job beta's own comment says the fixed slot was for.
    // Everything the port depends on is unchanged: the order, the heights, and
    // the running line drawn at bar centres.
    const slot = CL_W / n;
    const bw = Math.max(2, Math.min(CL_BAR_MAX, slot * 0.7));
    const left = (i: number): number => i * slot + (slot - bw) / 2;
    // 🚨 THE DASHED LINE IS DRAWN AT BAR CENTRES, not at full-width positions.
    // Beta learned this one and says so in its own comment: a line laid out
    // across the whole box drifts away from left-pinned bars as the card
    // narrows, so the running total stops lining up with the loss that stepped
    // it.
    const mid = (i: number): number => left(i) + bw / 2;
    const run = series
      .map((pt, i) => `${mid(i).toFixed(1)},${y(pt.cumulative).toFixed(1)}`).join(' ');
    return (
      <svg viewBox={`0 0 ${CL_W} ${CL_H}`} preserveAspectRatio="none" role="img"
        aria-label="Every preventable loss, oldest on the left, with the running total">
        {series.map((pt, i) => (
          <rect key={i} x={left(i).toFixed(1)} y={y(pt.amount).toFixed(1)}
            width={bw.toFixed(1)} height={Math.max(1, CL_H - y(pt.amount)).toFixed(1)}
            fill={pt.source === 'auto' ? 'var(--orange)' : 'var(--gold)'} fillOpacity="0.85">
            <title>{clTitle(pt)}</title>
          </rect>
        ))}
        <polyline points={run} fill="none" stroke="var(--orange)" strokeWidth="1.5"
          strokeDasharray="3 3" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
    );
  })() : (() => {
    const x = (i: number): number => (i / (n - 1)) * CL_W;
    const line = series
      .map((pt, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(pt.cumulative).toFixed(1)}`)
      .join(' ');
    const area = `${line} L${CL_W},${CL_H} L0,${CL_H} Z`;
    // ⚠ HOVER TARGETS, NOT MARKERS. Beta draws a dot at every point; a dot in a
    // box stretched to the card's width is drawn as an ellipse, so the trade
    // behind each step is reachable here through an invisible full-height slice
    // instead. Same information, and nothing on screen is the wrong shape.
    const slice = CL_W / n;
    return (
      <svg viewBox={`0 0 ${CL_W} ${CL_H}`} preserveAspectRatio="none" role="img"
        aria-label="What preventable losses have cost, running total, oldest on the left">
        <defs>
          <linearGradient id="an-cl-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--orange)" stopOpacity="0.32" />
            <stop offset="1" stopColor="var(--orange)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#an-cl-fill)" />
        <path d={line} fill="none" stroke="var(--orange)" strokeWidth="2"
          strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {series.map((pt, i) => (
          <rect key={i} x={(i * slice).toFixed(1)} y="0" width={slice.toFixed(1)}
            height={CL_H} fill="transparent">
            <title>{clTitle(pt)}</title>
          </rect>
        ))}
      </svg>
    );
  })();

  return (
    <div className="card an-chart" data-testid="an-cl-chart">
      <div className="an-heat-head">
        <h3 className="gold">What it has cost you so far</h3>
        {single ? null : (
          <div className="exc-groupbar">
            {CL_VIEWS.map((v) => (
              <button key={v.id} type="button"
                className={`period-chip${shown === v.id ? ' active' : ''}`}
                aria-pressed={shown === v.id}
                onClick={() => setView(v.id)}
              >{v.label}</button>
            ))}
          </div>
        )}
      </div>
      {chart}
      <p className="muted an-chart-foot">
        Oldest on the left. {n} preventable {n === 1 ? 'loss' : 'losses'} cost{' '}
        <b className="red">{moneyPlain(cl.total, 0)}</b> over this period, and the
        line ends on that same figure — it is the running total the card above is
        built from, not a second sum. Hover any{' '}
        {shown === 'per-trade' ? 'bar' : 'point'} for the trade behind it.
        {single
          ? ' One loss has no running total to draw, so there is nothing to switch between yet.'
          : ''}
        {shown === 'per-trade' && byHand > 0
          ? ` Gold bars are the ${byHand} you flagged by hand; orange ones a rule caught.`
          : ''}
      </p>
    </div>
  );
}

const ordinal = (n: number): string => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`);

/** ⚠ Zero is neither. A flat average after the mistake is not good news. */
function signClass(n: number): string {
  if (n > 0) return 'blue';
  if (n < 0) return 'red';
  return '';
}

function TiltCard({ tilt }: { tilt: TiltSignal }) {
  const bar = (label: string, v: number, n: number, tone: string) => (
    <div className="an-bar2">
      <div className="an-bar2-l">{label}</div>
      <div className="an-bar2-t">
        <span className="an-bar2-f" data-tone={tone} style={{ width: `${v.toFixed(0)}%` }} />
      </div>
      <div className="an-bar2-v">{pct(v, 0)} <span className="muted">· {n}t</span></div>
    </div>
  );

  return (
    <div className="card" data-testid="an-tilt">
      <h3>Tilt — how you trade the next one</h3>
      {tilt.drop == null ? (
        <p className="muted">
          Not enough sequenced trades yet — this needs at least {TILT_MIN_SAMPLE} trades
          after a win and {TILT_MIN_SAMPLE} after a loss. So far:{' '}
          {tilt.afterWinSample} and {tilt.afterLossSample}.
        </p>
      ) : (
        <>
          {bar('After a win', tilt.afterWin as number, tilt.afterWinSample, 'up')}
          {bar('After a loss', tilt.afterLoss as number, tilt.afterLossSample,
            (tilt.afterLoss as number) < (tilt.afterWin as number) ? 'down' : 'up')}
          <p className={`an-verdict${tilt.verdict === 'tilt' ? ' warn' : ''}`}>
            {tilt.verdict === 'tilt'
              ? <><b>{Math.round(tilt.drop)}-point drop after a loss.</b> That is a revenge
                signal — the leak is not in the losing trade, it is in how you take the
                next setup once you are red.</>
              : tilt.verdict === 'tightens-up'
                ? <>You win <b>more</b> after a loss, by {Math.round(-tilt.drop)} points.
                  No tilt — you tighten up when you are behind.</>
                : <>Your win rate holds steady after a loss
                  ({tilt.drop >= 0 ? '+' : ''}{Math.round(tilt.drop)} points). No tilt signal.</>}
          </p>
        </>
      )}
      <p className="muted exc-note">
        The share of trades that won, split by whether the trade before it won or
        lost. A flat trade in between does not count as a reset — you are still
        trading after that loss.
      </p>
    </div>
  );
}

function MistakeCard({ rows }: { rows: readonly MistakeRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.trades));
  return (
    <div className="card" data-testid="an-mistakes">
      <h3>What you keep doing</h3>
      {rows.length === 0 ? (
        <p className="muted">
          Nothing tagged in this period. The revenge, FOMO and overtraded flags on
          the trade form feed this, as do your own mistake tags.
        </p>
      ) : (
        <>
          {rows.map((r) => (
            <div className="an-bar2" key={r.label}>
              <div className="an-bar2-l">{r.label}</div>
              <div className="an-bar2-t">
                <span className="an-bar2-f" data-tone="warn"
                  style={{ width: `${((r.trades / max) * 100).toFixed(0)}%` }} />
              </div>
              <div className="an-bar2-v">{r.trades}</div>
            </div>
          ))}
          <p className="muted exc-note">
            Ranked by how many <b>trades</b> carry each one. A trade flagged both
            by a checkbox and by your own tag counts once — otherwise annotating
            thoroughly would look like trading badly.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The timing heatmap
// ---------------------------------------------------------------------------

const HEAT_METRICS: readonly { id: HeatMetric; label: string; blurb: string }[] = [
  { id: 'pnl', label: 'P&L', blurb: 'what that hour is worth to you' },
  { id: 'trades', label: 'Trades', blurb: 'how much you trade then' },
  { id: 'winRate', label: 'Win rate', blurb: 'how often it works then' },
];

function HeatmapCard(
  { grid, metric, onMetric, unit }: {
    grid: HeatGrid; metric: HeatMetric; onMetric: (m: HeatMetric) => void;
    /**
     * 🚨 ADDED AFTER A HALF-DONE FIX OF MY OWN (#67).
     *
     * The first pass converted the grid's VALUES to the display unit and left
     * both the cell text and the tooltip on `money()` — so the bug changed
     * direction rather than going away: it had been dollars labelled as points,
     * and became points labelled as dollars. Exactly the failure this issue is
     * about, committed while fixing it.
     */
    unit: PnlUnit;
  },
) {
  const max = heatMax(grid, metric);
  const active = HEAT_METRICS.find((m) => m.id === metric);

  return (
    <div className="card" data-testid="an-heatmap">
      <div className="an-heat-head">
        <h3>Day of week × hour of day</h3>
        <div className="exc-groupbar">
          {HEAT_METRICS.map((m) => (
            <button key={m.id} type="button"
              className={`period-chip${metric === m.id ? ' active' : ''}`}
              aria-pressed={metric === m.id}
              onClick={() => onMetric(m.id)}
            >{m.label}</button>
          ))}
        </div>
      </div>

      {grid.placed === 0 ? (
        <p className="muted">
          Nothing here can be placed on a clock. A trade needs a time-in to have
          an hour, and imported trades often arrive without one —{' '}
          <b>{grid.unplaced}</b> in this period {grid.unplaced === 1 ? 'is' : 'are'} missing it.
        </p>
      ) : (
        <>
          <div className="an-heat-scroll">
            <div className="an-heat"
              style={{ gridTemplateColumns: `56px repeat(${grid.hours.length}, minmax(28px, 1fr))` }}>
              <div />
              {grid.hours.map((h) => (
                <div className="an-heat-h" key={`h${h}`}>{hourLabel(h)}</div>
              ))}
              {grid.dows.map((d) => (
                <Fragment key={`d${d}`}>
                  <div className="an-heat-d">{DOW_LABELS[d]}</div>
                  {grid.hours.map((h) => {
                    const c = grid.cells[cellKey(d, h)];
                    if (!c) return <div className="an-heat-cell empty" key={`${d}-${h}`} />;
                    const v = heatValue(c, metric);
                    // ⚠ 0.18 FLOOR. A cell with a real trade in it has to look
                    // different from an empty one even when its value is tiny.
                    const alpha = 0.18 + heatIntensity(c, metric, max) * 0.55;
                    const sign = metric === 'trades' ? 1 : v > 0 ? 1 : v < 0 ? -1 : 0;
                    return (
                      <div className="an-heat-cell" key={`${d}-${h}`}
                        data-sign={sign > 0 ? 'up' : sign < 0 ? 'down' : 'flat'}
                        style={{ '--heat-a': String(alpha) } as CSSProperties}
                        title={`${DOW_LABELS[d]} ${hourLabel(h)} — ${c.count} trade${c.count === 1 ? '' : 's'} · ${unitValue(c.pnl, unit, 0)} · ${c.wins}W ${c.losses}L`}
                      >{heatCellText(c, metric, unit)}</div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>

          <p className="muted exc-note">
            Each cell is one hour of one weekday, shaded by <b>{active?.blurb}</b>.
            The day is the <b>session</b> the trade belongs to, and the hour is
            the clock time you opened it — so a trade taken at 11pm on Monday
            night sits in Tuesday’s row, in the 11pm column. Hover any cell for
            its trade count and win/loss split.
            {metric === 'winRate' ? (
              <> ⚠ A cell holding one trade reads 0% or 100%, and shades as
                strongly as one holding twenty. Check the count before believing
                a corner of this grid.</>
            ) : null}
            {grid.unplaced > 0 ? (
              <> <b>{grid.unplaced}</b> trade{grid.unplaced === 1 ? '' : 's'} in
                this period {grid.unplaced === 1 ? 'has' : 'have'} no time-in and
                {grid.unplaced === 1 ? ' is' : ' are'} not on this grid at all.</>
            ) : null}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * ⚠ Thousands are abbreviated so a `+$1,325.00` cannot widen its whole column
 * and push the grid past the card — the old client learned this one.
 *
 * ⚠ IN DOLLARS ONLY, which is beta's rule and not an oversight: a thousand
 * POINTS is a number a trader reads exactly, and "+1.3k" throws away the
 * precision that makes it useful. Beta prints `fmtPnlUnit(v, unit, unit === 'r'
 * ? 1 : 0)` for every other unit, unabbreviated.
 */
function heatCellText(
  c: { count: number; wins: number; losses: number; pnl: number },
  metric: HeatMetric,
  unit: PnlUnit,
): string {
  if (metric === 'trades') return String(c.count);
  if (metric === 'winRate') {
    const decided = c.wins + c.losses;
    return decided ? `${Math.round((100 * c.wins) / decided)}%` : '—';
  }
  const v = c.pnl;
  if (unit !== 'usd') return unitValue(v, unit, unit === 'r' ? 1 : 0);
  if (Math.abs(v) >= 1000) {
    return `${v > 0 ? '+' : v < 0 ? '-' : ''}$${Math.round(Math.abs(v) / 100) / 10}k`;
  }
  return money(v, 0);
}

/** A plain figure box. ⚠ Deliberately NOT the `an-kpi` tile — those carry an
 *  accent bar and a sparkline slot, and a card of four empty sparklines reads as
 *  four charts that failed to load. */
function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="an-tile">
      <div className="an-tile-l">{label}</div>
      <div className="an-tile-v">{value}</div>
      <div className="an-tile-s">{sub}</div>
    </div>
  );
}

/** ⚠ No sign on a flat R, in either direction — the same rule the extension
 *  card learned. `+0.00R` reads as a small win. */
function signedR(v: number): string {
  const s = num(v, 2);
  const flat = Number(s.replace(/,/g, '')) === 0;
  return `${!flat && v > 0 ? '+' : ''}${flat ? s.replace(/^-/, '') : s}R`;
}

function shiftDays(iso: string, by: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + by);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// The hero
// ---------------------------------------------------------------------------

/**
 * The period's headline figure.
 *
 * ⚠ THE WHOLE CARD CHANGES COLOUR WITH THE SIGN — the glow, the border and the
 * eyebrow, not just the number. In this theme positive is BLUE and negative is
 * ORANGE, which is the old client's palette and not a mistake for green/red.
 */
function Hero(
  {
    label, netPnl, trendPct, equity, count, winRate, avgR, avgRSample, profitFactor,
    maxDrawdown, unit, mixed, valued,
  }: {
    label: string; netPnl: number; trendPct: number | null; equity: readonly number[];
    unit: PnlUnit;
    /** points/ticks across more than one instrument — the total means nothing. */
    mixed: boolean;
    /** How many trades had a value in this unit, of `count`. */
    valued: number;
    count: number; winRate: number; avgR: number | null; avgRSample: number;
    profitFactor: number; maxDrawdown: number;
  },
) {
  const loss = netPnl < 0;
  return (
    <div className={`an-hero${loss ? ' loss' : ''}`} data-testid="an-hero">
      <div className="an-hero-eyebrow">Period performance · {label}</div>
      <h2 className={`an-hero-headline${loss ? ' loss' : ''}`}>{unitValue(netPnl, unit)}</h2>
      {/* 🚨 THE TWO WARNINGS THE DOMAIN LAYER HAS ALWAYS RAISED AND NOTHING READ.
          `mixed`: ES points plus CL points is a sum with no meaning. `valued`:
          in R a stop-less trade drops out of the total while still counting as
          a trade, so the headline covers fewer trades than the line under it
          claims. Same wording and colour as the Dashboard. */}
      {mixed || valued !== count ? (
        <div className="an-hero-caveat">
          {mixed ? <span>⚠ mixed instruments</span> : null}
          {valued !== count ? <span>{valued} of {count} priced in this unit</span> : null}
        </div>
      ) : null}
      {trendPct == null ? null : (
        <span className={`an-hero-trend${trendPct >= 0 ? '' : ' down'}`}>
          <span aria-hidden="true">{trendPct >= 0 ? '▲' : '▼'}</span>{' '}
          {trendPct >= 0 ? '+' : ''}{trendPct.toFixed(0)}% vs the period before
        </span>
      )}
      <div className="an-hero-stats">
        <div className="an-hero-stat"><b>{count}</b> trades</div>
        <div className="an-hero-stat"><b>{pct(winRate, 0)}</b> win rate</div>
        {/* ⚠ AN EM DASH WHEN NOTHING RECORDED AN R. The old client prints
            "+0.00R avg R" there — a measured-looking flat reading over a
            population of zero. PD-16. */}
        <div className="an-hero-stat">
          <b>{avgR == null ? '—' : `${avgR >= 0 ? '+' : ''}${num(avgR, 2)}R`}</b> avg R
          {avgR != null && avgRSample < count
            ? <span className="muted"> (of {avgRSample})</span> : null}
        </div>
        <div className="an-hero-stat">
          <b>{profitFactor === Infinity ? '∞' : num(profitFactor, 2)}</b> profit factor
        </div>
        <div className="an-hero-stat"><b>{unitValue(-Math.abs(maxDrawdown), unit)}</b> max drawdown</div>
      </div>
      <SparkLine values={equity} className="an-hero-sparkline" w={280} h={90}
        color={loss ? 'var(--orange)' : 'var(--blue)'} label="Equity through the period" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The four tiles
// ---------------------------------------------------------------------------

// ⚠ NAMED, because two bare numbers in a ternary is how a threshold gets "tidied"
// to a rounder figure by someone who never saw where it came from. Both are
// beta's, verbatim (app/index.html:44135).
const WIN_RATE_GOOD = 55;
const WIN_RATE_POOR = 45;

function KpiTiles(
  {
    count, tradingDays, dailyCounts, winRate, wins, losses, periodPnl,
    winRateSeries, equity, maxDrawdown, unit,
  }: {
    unit: PnlUnit;
    count: number; tradingDays: number; dailyCounts: readonly number[];
    winRate: number; wins: number; losses: number;
    /** Trade-only P&L for the period — see the note at the call site. */
    periodPnl: number;
    winRateSeries: readonly number[];
    equity: readonly number[]; maxDrawdown: number;
  },
) {
  const peak = peakEquity(equity);
  return (
    /* ⚠ EACH TILE OPENS WITH ITS ICON, before the label, as beta has it. The
       four glyphs are beta's own KPI set and NOT its sidebar set — see the
       `kpi*` block in `shell/icons.ts` for why that distinction matters. */
    <div className="an-kpi-grid" data-testid="an-kpis">
      <div className="an-kpi" data-accent="purple">
        <div className="an-kpi-icon"><Icon name="kpiTrades" /></div>
        <div className="an-kpi-label">Trades</div>
        <div className="an-kpi-value">{count}</div>
        <SparkBars values={dailyCounts} className="an-kpi-spark"
          label="Trades per day through the period" />
        <div className="an-kpi-sub">{tradingDays} trading day{tradingDays === 1 ? '' : 's'}</div>
      </div>

      <div className="an-kpi" data-accent="blue">
        <div className="an-kpi-icon"><Icon name="kpiWinRate" /></div>
        <div className="an-kpi-label">Win rate</div>
        {/* ── ⚠ RED NEEDS THE P&L TOO, AND THAT IS THE WHOLE POINT ──
            Beta: `winRate >= 55 ? 'win' : (winRate < 45 && periodPnl < 0) ? 'loss' : ''`
            (app/index.html:44135). A sub-45% win rate with the period PROFITABLE
            stays uncoloured, because a low hit rate with big winners is a
            strategy, not a fault. Colouring on the rate alone would tell a
            trader who made money that they are failing — which is the one thing
            this tile must never do. */}
        <div className={`an-kpi-value${
          winRate >= WIN_RATE_GOOD ? ' win'
            : (winRate < WIN_RATE_POOR && periodPnl < 0) ? ' loss' : ''}`}>
          {pct(winRate, 0)}
        </div>
        {/* ⚠ THE RUNNING rate, not the daily one — a one-trade day is 0% or
            100% and plots as noise. It converges on the figure above it. */}
        <SparkLine values={winRateSeries} className="an-kpi-spark" w={100} h={28}
          color="var(--blue)" label="Win rate as it stood each day" />
        <div className="an-kpi-sub">{wins}W / {losses}L</div>
      </div>

      <div className="an-kpi" data-accent="blue">
        <div className="an-kpi-icon"><Icon name="kpiPeak" /></div>
        <div className="an-kpi-label">Peak equity</div>
        <div className="an-kpi-value win">{peak == null ? '—' : unitValue(peak, unit)}</div>
        <SparkLine values={runningPeak(equity)} className="an-kpi-spark" w={100} h={28}
          color="var(--blue)" label="The high-water mark through the period" />
        <div className="an-kpi-sub">{peak == null ? 'no curve yet' : 'period high-water'}</div>
      </div>

      <div className="an-kpi" data-accent="orange">
        <div className="an-kpi-icon"><Icon name="kpiDd" /></div>
        <div className="an-kpi-label">Max drawdown</div>
        <div className="an-kpi-value loss">{unitValue(-Math.abs(maxDrawdown), unit)}</div>
        {/* ⚠ The UNDERWATER series, not the equity one — how far below the
            high-water mark you were at each point, which is the shape the
            figure above summarises. */}
        <SparkLine values={underwater(equity)} className="an-kpi-spark" w={100} h={28}
          color="var(--orange)" label="How far below the high-water mark" />
        <div className="an-kpi-sub">peak to trough</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sparklines
// ---------------------------------------------------------------------------

/**
 * ⚠ ONE POINT IS NOT A LINE, and this renders nothing rather than a flat stroke
 * across the tile. A single trade drawn as a horizontal line says "this went
 * nowhere all period", which is a claim about a period that has not happened.
 */
function SparkLine(
  { values, w, h, color, className, label }: {
    values: readonly number[]; w: number; h: number;
    color: string; className: string; label: string;
  },
) {
  if (values.length < 2) return null;
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = (max - min) || 1;
  const step = w / (values.length - 1);
  const y = (v: number): number => h - ((v - min) / span) * h;
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg className={className} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
      role="img" aria-label={label}>
      <path d={area} fill={color} fillOpacity="0.12" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx={w} cy={y(values[values.length - 1] as number)} r="2.5" fill={color} />
    </svg>
  );
}

/** Counts, so bars rather than a line — a trade count between two days is not a
 *  quantity that existed. */
function SparkBars(
  { values, className, label }: { values: readonly number[]; className: string; label: string },
) {
  if (!values.length) return null;
  const w = 100; const h = 28;
  const max = Math.max(1, ...values);
  const bw = w / values.length;
  return (
    <svg className={className} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
      role="img" aria-label={label}>
      {values.map((c, i) => {
        const bh = (c / max) * h;
        return (
          <rect key={i} x={i * bw + bw * 0.15} y={h - bh} width={bw * 0.7} height={bh}
            rx="1" fill="var(--purple)" opacity="0.65" />
        );
      })}
    </svg>
  );
}

/**
 * The analytics period chips. Exported so every analytics sub-tab uses ONE bar.
 *
 * ⚠ Setup Quality still writes its own copy without the custom-range fields.
 * That divergence predates this export and is not fixed by it — noted so the
 * next reader does not assume "shared component" means "shared everywhere".
 */
export function PeriodBar(
  { period, onPick }: { period: AnalyticsPeriod; onPick: (p: AnalyticsPeriod) => void },
) {
  const custom = period.id === 'custom';
  return (
    <div className="an-periodbar">
      {ANALYTICS_PERIODS.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`period-chip${period.id === p.id ? ' active' : ''}`}
          aria-pressed={period.id === p.id}
          onClick={() => onPick({ id: p.id })}
        >
          {p.label}
        </button>
      ))}
      <button
        type="button"
        className={`period-chip${custom ? ' active' : ''}`}
        aria-pressed={custom}
        onClick={() => onPick({ id: 'custom', customFrom: null, customTo: null })}
      >
        Custom
      </button>

      {custom ? (
        <>
          <input
            type="date" aria-label="Analytics start"
            value={period.customFrom ?? ''}
            onChange={(e) => onPick({ ...period, id: 'custom', customFrom: e.target.value })}
          />
          <span aria-hidden="true">→</span>
          <input
            type="date" aria-label="Analytics end"
            value={period.customTo ?? ''}
            onChange={(e) => onPick({ ...period, id: 'custom', customTo: e.target.value })}
          />
        </>
      ) : null}
    </div>
  );
}

/**
 * One card, two readings of the same walk.
 *
 * ⚠ Deliberately not a charting library. The old client draws its own SVG and the
 * shape here is the same data; adding a dependency for one chart would be a
 * decision to make once, for all charts, not sideways during a tab port.
 *
 * ⚠ THE DRAWDOWN IS A SEPARATE CARD, not a second line on the equity one. They
 * share an x-axis and nothing else: equity is signed and centred on zero, the
 * drawdown is a magnitude that only ever hangs below it. Overlaid, the shallow
 * end of one is the deep end of the other.
 */
function CurveCard(
  { title, points, kind, unit }: {
    title: string; points: readonly EquityPoint[]; kind: 'equity' | 'drawdown';
    unit: PnlUnit;
  },
) {
  const W = 560;
  const H = 220;
  const dd = kind === 'drawdown';

  const body = (() => {
    if (points.length < 2) {
      // ⚠ One point is not a curve. A flat line from a single trade implies a
      // period that went nowhere.
      return <p className="muted">Two or more valued trades are needed to draw a curve.</p>;
    }
    const vals = points.map((p) => (dd ? p.drawdown : p.equity));
    // ⚠ ZERO IS ALWAYS IN FRAME. On the drawdown chart that is the whole point:
    // scaling to the data alone would make a $40 dip fill the card exactly as a
    // $6,000 one does.
    const lo = Math.min(0, ...vals);
    const hi = Math.max(0, ...vals);
    const span = (hi - lo) || 1;
    const x = (i: number): number => (i / (points.length - 1)) * W;
    const y = (v: number): number => H - ((v - lo) / span) * H;

    const line = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const area = `${line} L${W},${y(0).toFixed(1)} L0,${y(0).toFixed(1)} Z`;

    /**
     * ⚠ THE EQUITY CURVE CHANGES COLOUR AT THE ZERO LINE, and that is carrying
     * meaning rather than decorating: it says at a glance which side of
     * break-even the account was on at each point. A single colour chosen by the
     * FINAL value paints a period that spent months in profit entirely red
     * because it ended down. Two gradient stops at the same offset make the
     * change a hard edge rather than a fade. D119 — a colour is part of what
     * "relocated" relocates.
     *
     * ⚠ The drawdown chart stays one colour, because every value on it is ≤ 0.
     */
    const zeroFrac = Math.min(1, Math.max(0, y(0) / H));
    const gid = `an-grad-${kind}`;
    const last = vals[vals.length - 1] as number;

    return (
      <>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
          aria-label={dd ? 'Drawdown through the period'
            : 'Cumulative equity through the period'}>
          <defs>
            <linearGradient id={`${gid}-line`} x1="0" y1="0" x2="0" y2="1">
              <stop offset={zeroFrac} stopColor={dd ? 'var(--red)' : 'var(--blue)'} />
              <stop offset={zeroFrac} stopColor="var(--red)" />
            </linearGradient>
            <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={dd ? 'var(--red)' : 'var(--blue)'} stopOpacity="0.30" />
              <stop offset={zeroFrac} stopColor={dd ? 'var(--red)' : 'var(--blue)'} stopOpacity="0.05" />
              <stop offset={zeroFrac} stopColor="var(--red)" stopOpacity="0.05" />
              <stop offset="1" stopColor="var(--red)" stopOpacity="0.34" />
            </linearGradient>
          </defs>
          <line x1="0" y1={y(0)} x2={W} y2={y(0)} stroke="var(--border-1)" strokeWidth="1" />
          <path d={area} fill={`url(#${gid}-fill)`} />
          <path d={line} fill="none" stroke={`url(#${gid}-line)`} strokeWidth="2"
            strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <p className="muted an-chart-foot">
          {dd
            ? <>Deepest: <b className="red">{unitValue(-Math.abs(Math.min(...vals)), unit)}</b> below the
              high-water mark. Back to zero means a new high.</>
            : <>Closed the period at <b className={last >= 0 ? 'blue' : 'red'}>{unitValue(last, unit)}</b>.</>}
        </p>
      </>
    );
  })();

  return (
    <div className="card an-chart" data-testid={`an-curve-${kind}`}>
      {/* ⚠ The drawdown heading is RED in the old client, and that is deliberate
          rather than decorative — it is the only card title on the tab that is
          coloured, because it is the only one that is always bad news. */}
      <h3 className={dd ? 'red' : undefined}>{title}</h3>
      {body}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Risk-adjusted returns
// ---------------------------------------------------------------------------

const TIER_WORD: Record<string, string> = {
  strong: 'Strong', good: 'Good', marginal: 'Marginal', weak: 'Weak', ungraded: 'n/a',
};

/** ⚠ Literal class names, one per band — a computed `tier-${x}` is a class the
 *  style guard cannot read. */
const tierClass = (t: string): string =>
  (t === 'strong' || t === 'good') ? 'good' : t === 'marginal' ? 'warn' : t === 'weak' ? 'weak' : 'muted';

function RiskCard({ risk }: { risk: RiskAdjusted }) {
  const sample = risk.tradingDays < MIN_TRADING_DAYS
    ? `needs ${MIN_TRADING_DAYS} trading days, has ${risk.tradingDays}`
    : `${risk.tradingDays} trading day${risk.tradingDays === 1 ? '' : 's'} of P&L`;

  return (
    <div className="card" data-testid="an-risk">
      <h3>Risk-adjusted returns</h3>
      <div className="an-risk-grid">
        <RiskTile label="Sharpe" reading={risk.sharpe}
          sub={risk.sharpe.degenerate ? 'too little variance to grade' : sample} />
        <RiskTile label="Sortino" reading={risk.sortino}
          sub={risk.sortino.degenerate ? 'too little variance to grade' : sample} />
        <RiskTile label="Calmar" reading={risk.calmar}
          sub={risk.calmar.degenerate ? 'drawdown too small to measure'
            : risk.daysSpanned < 14 ? `needs 14 days of history, has ${risk.daysSpanned}`
              : 'annualised, against your worst drawdown'} />
      </div>

      <RiskVerdictLine verdict={risk.verdict} />

      <p className="muted exc-note">
        All three read your <b>daily</b> P&amp;L over the days you actually
        traded, annualised. <b>Sharpe</b> divides your average day by how much it
        swings — <i>all</i> the swing, up as well as down. <b>Sortino</b> divides
        it by the downside only, because upside volatility is not a risk anyone
        needs protecting from. <b>Calmar</b> is the bluntest: a year of this
        return against your worst drawdown. Sharpe of 1 is good and 2 is strong;
        Sortino runs a little higher by construction; Calmar of 3 is good.
      </p>
    </div>
  );
}

function RiskTile(
  { label, reading, sub }: { label: string; reading: RatioReading; sub: string },
) {
  const cls = tierClass(reading.tier);
  return (
    <div className={`an-risk-tile ${cls}`}>
      <div className="an-risk-label">{label}</div>
      {/* ⚠ NO NUMBER when the reading is ungradeable. "41.2 — n/a" invites
          keeping the 41.2 and discarding the caveat, which is how a figure
          whose caveat was computed and dropped ends up being quoted. */}
      <div className={`an-risk-value ${cls}`}>
        {reading.value == null ? '—' : num(reading.value, 2)}
      </div>
      <span className={`an-risk-tier ${cls}`}>{TIER_WORD[reading.tier]}</span>
      <div className="an-risk-sub">{sub}</div>
    </div>
  );
}

/**
 * 🚨 THE SENTENCE THE OLD CLIENT GETS BACKWARDS.
 *
 * Its verdict fires "your variance is dominated by upside, not downside (good
 * sign)" whenever `sortino > sharpe * 1.5`. On a losing book both ratios are
 * negative and the Sortino is the more negative one, so that comparison is true
 * by arithmetic — and it displaces the warning a sub-0.5 Sharpe should produce.
 * Josh's own book: −$3,278, Sharpe −1.32, and beta calls it a good sign. PD-19.
 */
function RiskVerdictLine({ verdict }: { verdict: RiskVerdict }) {
  const warn = verdict.kind === 'degenerate' || verdict.kind === 'not-compensating';
  const body = (() => {
    switch (verdict.kind) {
      case 'too-few-days':
        return <>Not enough sample yet — these settle down somewhere past thirty trading
          days, and there {verdict.tradingDays === 1 ? 'is' : 'are'} {verdict.tradingDays} here.</>;
      case 'degenerate':
        return <><b>These are not meaningful on this sample.</b> The curve is too smooth —
          almost no day-to-day swing and little or no drawdown — which inflates all three
          past the point of use. They settle into a real range once the window contains
          normal variance and at least one genuine drawdown.</>;
      case 'not-compensating':
        return <><b>The return is not paying for the swing.</b> That is what a Sharpe below
          0.5 means: the size of your daily ups and downs is not being rewarded. Worth
          looking at which setups you take and how you exit them before trading bigger.</>;
      case 'upside-dominant':
        return <><b>Risk-adjusted performance is {verdict.tier}.</b> Sortino
          ({num(verdict.sortino, 2)}) sits well above Sharpe ({num(verdict.sharpe, 2)}), which
          means most of your day-to-day swing is in the winning direction. That is a good
          sign — it is the up days that are moving, not the down ones.</>;
      case 'symmetric':
        return <><b>Risk-adjusted performance is {verdict.tier}.</b> Sharpe and Sortino are
          close together, so your good days and bad days swing about as far as each other.</>;
      default:
        return null;
    }
  })();
  if (!body) return null;
  return (
    <p className={`an-verdict${warn ? ' warn' : ''}`}>{body}</p>
  );
}

// 🔴 `BarRows` AND `Stat` WERE DELETED WITH THE "P&L BY WEEKDAY" CARD.
//
// That card was never on the old client's Analytics Overview — it belongs to the
// Dashboard, and Josh's 2026-08-06 screenshot is what showed it. Reading beta's
// SOURCE is how it got here in the first place; the screenshot is the gate that
// catches an invented control, and this is the third one it has caught.
//
// The old six-stat strip went with it: the hero and the four tiles carry those
// numbers now, and two renderings of one figure on one screen is D102.
